/**
 * @author Maxime Mustarda <maxime@inarix.com>
 * @file LicensePacksModal.tsx
 * @desc Created on Sat May 17 2025 02:00:21
 * @copyright All rights reserved @ Inarix
 */

import { FC, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Snackbar,
} from "@mui/material";
import {
  focusedLicense,
  selectAvailPacks,
  selectModalLicense,
} from "../../redux/slices/licenses/licenseList";
import { useTranslation } from "react-i18next";
import { renderLicenseCredits } from "./LicenceCredits";
import { bindPack } from "../../redux/actions/licenses/licenseListAsyncActions";
import { licensePacksModalStyles } from "./licenseStyles/LicensePacksModal";

export const LicensePacksModal: FC = () => {
  const packs = useSelector(selectAvailPacks);
  const license = useSelector(selectModalLicense);
  const dispatch = useDispatch();
  const [t] = useTranslation("global");
  const [checked, setChecked] = useState("");
  const [successOpen, setSuccessOpen] = useState(false);
  const checkHandler = (ev: any) => {
    setChecked(ev.target.value);
  };

  // type-safe if, won't ever happen
  if (!license) {
    return <></>;
  }

  return (
    <Box sx={licensePacksModalStyles.modalBox}>
      <div className="modal-detail-user-container">
        <div>
          <div className="username-title-modal">{t("licenses.addCreds")}</div>
        </div>

        <div
          className="access-and-permissions"
          style={licensePacksModalStyles.accessContainer}
        >
          <div className="access-title">{t("licenses.License")}</div>
          <div className="access-perm-info">
            <div className="access-values-users">
              <div className="option-left mt-2">
                <span className="title-view-span">
                  {t("Users_tables.columns_table_one.balance")}
                </span>
              </div>
              <div className="option-right">
                {renderLicenseCredits([license], t)}
              </div>
            </div>
            <div className="access-values-users">
              <div className="option-left">
                <span className="title-view-span">
                  {t("licenses.expiration")}
                </span>
              </div>
              <div className="option-right">
                {new Date(license.expiryDate).toLocaleDateString()}
              </div>
            </div>
            <div className="access-value-users">&nbsp;</div>
          </div>
          <div className="divider-user-details" />

          <FormControl sx={licensePacksModalStyles.formControl}>
            <RadioGroup onChange={checkHandler}>
              {packs.map((p) => (
                <FormControlLabel
                  label={`+ ${p.initialCredits} ${t("licenses.credits")} - ${t("licenses.pack_expiration")} : ${new Date(p.expiryDate).toLocaleDateString()}`}
                  control={<Radio value={p.id} />}
                  key={p.id}
                  slotProps={{
                    typography: { fontSize: "12px" },
                  }}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </div>

        <div className="buttons-user-view">
          <div className="container-buttons-users-actions">
            <Button
              disabled={successOpen}
              variant="outlined"
              onClick={() => dispatch(focusedLicense(""))}
              sx={licensePacksModalStyles.buttonTextTransform}
            >
              {t("Common_options.Cancel")}
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                (
                  dispatch(
                    bindPack({
                      licenseId: license.id,
                      packId: checked,
                    }) as any,
                  ) as any
                )
                  .unwrap()
                  .then(() => {
                    setSuccessOpen(true);
                    setTimeout(() => {
                      dispatch(focusedLicense(""));
                    }, 1500);
                  });
              }}
              sx={licensePacksModalStyles.buttonTextTransform}
              disabled={!checked || successOpen}
            >
              {t("Common_options.Add")}
            </Button>
          </div>
        </div>
      </div>
      <Snackbar
        open={successOpen}
        autoHideDuration={4000}
        onClose={() => setSuccessOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setSuccessOpen(false)} severity="success">
          {t("licenses.pack_added_success")}
        </Alert>
      </Snackbar>
    </Box>
  );
};
